import land from '../../src/atlas/land.json';
import { CHART, CHART_HEIGHT, proj, unitsPerNm } from '../../src/engine/geo';
import { h, svg } from '../../src/ui/dom';
import { MAP_NAMES, placeLabels } from './labels';
import { placeHref } from './nav';
import { destinationOf } from './places/destinations';
import { placeForPorts } from './places/places';
import type { RouteView, TrackView } from './view';

const NEAR_NM = 300;

/** The voyage map, always the whole voyage: country names, the planned route with a link at each stop, the track and the last position. */
export function drawTrack(root: HTMLElement, view: TrackView, opts: { plan?: RouteView | null } = {}): SVGSVGElement {
  const map = svg('svg', { id: 'map', class: 'map', role: 'img', 'aria-label': 'Map of the voyage with the boat\'s live position' });
  svg('rect', { x: -2000, y: -2000, width: 5000, height: 5000, class: 'sea' }, map);
  svg('path', { d: land.d, class: 'land' }, map);
  const labels = svg('g', { class: 'map-labels', 'aria-hidden': 'true' }, map);
  const planPts = (opts.plan?.line ?? []).map(([lon, lat]) => proj(lon, lat));
  const planLine = planPts.length ? svg('path', { class: 'plan-line', d: planPts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join('') }, map) : null;
  const stopDots: SVGCircleElement[] = [], stopHits: SVGCircleElement[] = [], stopPts: [number, number][] = [];
  for (const s of opts.plan?.stops ?? []) {
    const [x, y] = proj(s.lon, s.lat), dest = destinationOf(s), story = placeForPorts([s.port]);
    stopPts.push([x, y]);
    // Each stop links to its country's page, at its island's section when there is one.
    const href = dest ? `${placeHref(dest.slug)}${story ? `#s-${story.slug}` : ''}` : null;
    const g = href ? svg('a', { href, class: 'plan-stop', 'aria-label': `${s.label}: about ${dest!.name}` }, map) : svg('g', { class: 'plan-stop' }, map);
    svg('title', {}, g).textContent = dest ? `${s.label} · ${dest.name}` : s.label;
    stopHits.push(svg('circle', { cx: x.toFixed(1), cy: y.toFixed(1), class: 'stop-hit' }, g));
    stopDots.push(svg('circle', { cx: x.toFixed(1), cy: y.toFixed(1), class: 'stop-dot' }, g));
  }
  const pts = view.fixes.map(([, lat, lon]) => proj(lon, lat));
  const trk = svg('path', { class: 'trk', d: pts.length ? pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join('') : 'M0,0' }, map);
  if (!pts.length) trk.setAttribute('visibility', 'hidden');
  const boat = view.last ? proj(view.last.lon, view.last.lat) : null;
  const dot = boat ? svg('circle', { cx: boat[0].toFixed(1), cy: boat[1].toFixed(1), class: 'boat' }, map) : null;

  /** Country names sized in screen pixels, so they wait for layout and redraw when the map resizes. */
  const drawLabels = () => {
    labels.replaceChildren();
    const vb = map.viewBox.baseVal, width = map.getBoundingClientRect().width;
    if (!vb || !width) return;
    // Smaller type on a phone-sized map, so the names do not crowd the route.
    const px = (vb.width / width) * Math.min(1, Math.max(0.8, width / 1100)), r = 8 * (vb.width / 900);
    const cands = MAP_NAMES.filter(n => !(n.minor && width < 600)).map(n => { const [x, y] = proj(n.lon, n.lat); return { text: n.text, x, y, kind: n.kind }; });
    const dots = [...stopPts, ...(boat ? [boat] : [])].map(([x, y]) => [x, y, r] as [number, number, number]);
    for (const l of placeLabels(cands, px, dots, [vb.x, vb.y, vb.width, vb.height])) {
      svg('text', { x: l.x.toFixed(1), y: l.y.toFixed(1), class: `map-label ${l.kind}`, 'font-size': l.size.toFixed(2), 'stroke-width': (l.size * 0.3).toFixed(2) }, labels).textContent = l.text;
    }
  };
  new ResizeObserver(drawLabels).observe(map);

  const vb = [0, 0, CHART.width, CHART_HEIGHT];
  map.setAttribute('viewBox', vb.join(' '));
  const u = vb[2]! / 900;
  trk.setAttribute('stroke-width', String(2.4 * u));
  planLine?.setAttribute('stroke-width', String(1.8 * u));
  planLine?.setAttribute('stroke-dasharray', `${6 * u} ${4 * u}`);
  for (const c of stopDots) { c.setAttribute('r', String(4.5 * u)); c.setAttribute('stroke-width', String(1.5 * u)); }
  for (const c of stopHits) c.setAttribute('r', String(12 * u));
  dot?.setAttribute('r', String(6 * u));
  dot?.setAttribute('stroke-width', String(2.5 * u));
  root.append(h('div', { class: 'map-frame' }, map));
  drawLabels();
  return map;
}
